// @givmo/app-admin — Settings → Security → Company sign-in → SIGN OUT EVERY EMPLOYEE (AS-WEB7).
//
// AS-BE19's workforce session reset: the employer's blunt post-incident control. One button, one
// confirm, and a count of what it did.
//
// ══ ★ THE CONFIRM STATES THE SCOPE IN BOTH DIRECTIONS ═════════════════════════════════════════
// What it REACHES and what it LEAVES ALONE, because an Owner needs both to press it correctly:
//
//   • It reaches the ROSTER — every employee linked to the company, whether or not they ever
//     completed an SSO ceremony. The narrower reading ("employees using SSO") is the one that would
//     leave an Owner told "done" while every un-linked-identity account stayed signed in.
//   • It does NOT reach company admins who are not linked employees, so the person pressing it
//     almost certainly stays signed in — worth saying, because the opposite assumption stops people
//     pressing a button they should.
//   • It touches SESSIONS AND THE AI CONNECTIONS THEY FEED (AS-BE55: each employee's AI assistants and
//     partner apps, and every AI connected to the company) AND NOTHING ELSE. No factor unenrolled, no
//     provider unlinked, no recovery code spent, no lock claim written, no employment ended. That is a boundary the backend holds,
//     not a reassurance this screen invented, and it is the half an Owner is most likely to assume
//     wrongly.
//
// The identity-provider sentence renders only under a STORED sign-in lock (`signin_lock`, AS-WEB22),
// because everywhere else the claim is false: at L0 nothing sends anyone anywhere, and at
// `employer_context` ("Available for employees") the tier's own words say their personal sign-in
// keeps working. A stored lock is in force whatever either flag says — the flags gate the surfaces
// that WRITE a tier, never the tier, and the blocking function reads the claim alone (backend
// `lock_claims.py`) — so the stored tier is the whole condition.
//
// ══ THE COUNTS ARE REPORTED, INCLUDING THE BAD ONE ════════════════════════════════════════════
// ⚠ `failed` is served precisely because it is ACTIONABLE — the reset is best-effort per employee and
// revocation is idempotent, so the remedy for a non-zero count is to press it again. Showing only
// `revoked` would let a partial run read as a complete one, which on an incident control is the
// dishonest direction. And a 200 that carried NO numbers is its own state: the tolerant reader turns
// a `{}` body into a perfectly defined all-`null` outcome, so the screen says what it does and does
// not know rather than rendering a reassuring zero.

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { isGivmoError } from '@givmo/api-client';
import { Button, Card, CardBody, CardHeader, Dialog, testIdProp } from '@givmo/design-system';
import { SSO_COPY, SSO_COPY_DRAFT } from './security-copy';
import { SsoErrorNote } from './sso-error-note';
import { WRAPPING_CARD_TITLE } from './card-title';
import {
  readSessionReset as decodeSessionReset,
  resetCountsComplete,
  resetHadFailures,
  type SessionResetOutcome,
} from './company-security-decode';
import { resetSsoSessions } from './company-security-transport';
import type { SsoTier } from './sso-decode';

const PREFIX = 'settings-security-session-reset';

export interface SessionResetCardProps {
  /** The STORED tier. Only a stored `signin_lock` makes the identity-provider sentence true. */
  mode: SsoTier | null;
}

export function SessionResetCard({ mode }: SessionResetCardProps) {
  const [confirming, setConfirming] = useState(false);
  const [outcome, setOutcome] = useState<SessionResetOutcome | null>(null);
  const [dark, setDark] = useState(false);
  const [forbidden, setForbidden] = useState(false);

  const reset = useMutation({
    mutationFn: resetSsoSessions,
    onSuccess: (body) => {
      setConfirming(false);
      setDark(false);
      setForbidden(false);
      setOutcome(decodeSessionReset(body));
    },
    onError: (error) => {
      // The surface can go dark between a page load and a click; a Member reaching a write gets 403.
      const withdraws = isGivmoError(error) && (error.status === 404 || error.status === 403);
      if (isGivmoError(error) && error.status === 404) setDark(true);
      if (isGivmoError(error) && error.status === 403) setForbidden(true);
      // ★ AND THE DIALOG MUST CLOSE, which is not the same as latching the state. Both notices render
      // in the CARD body, and the in-dialog error is deliberately suppressed while withdrawn (the raw
      // refusal must not double the notice) — so leaving the modal up puts the only explanation BEHIND
      // its own scrim. The Owner would see a dialog that did nothing and no reason why. Success
      // already closes it; these two failures have to as well.
      //
      // ⚠ jsdom cannot see this: the notice IS "in the document", just underneath a full-viewport
      // backdrop. Its covering test asserts the dialog is ABSENT rather than that the notice exists.
      if (withdraws) setConfirming(false);
    },
  });

  const withdrawn = dark || forbidden;

  return (
    <Card>
      <CardHeader
        className={WRAPPING_CARD_TITLE}
        title={SSO_COPY.resetTitle}
        subtitle={SSO_COPY.resetSubtitle}
      />
      <CardBody>
        <div className="flex flex-col gap-12" {...testIdProp(PREFIX)}>
          {!withdrawn && (
            <div>
              <Button
                appearance="saas"
                tone="secondary"
                onClick={() => setConfirming(true)}
                testId={`${PREFIX}-open`}
              >
                {SSO_COPY.resetButton}
              </Button>
            </div>
          )}

          {outcome !== null && <ResetOutcome outcome={outcome} />}

          {/* Not while withdrawn: the 404 and the 403 each have their own considered notice below,
              and rendering the raw refusal too would answer one press two contradictory ways. */}
          {!withdrawn && reset.isError && (
            <SsoErrorNote
              error={reset.error}
              fallback={SSO_COPY.resetFailed}
              testId={`${PREFIX}-error`}
            />
          )}

          {dark && (
            <p
              className="font-sans text-s13 font-w400 leading-h15 tracking-normal text-text-secondary"
              {...testIdProp(`${PREFIX}-unavailable`)}
            >
              {SSO_COPY.unavailable}
            </p>
          )}

          {forbidden && (
            <p
              className="font-sans text-s13 font-w500 tracking-normal text-danger-700"
              {...testIdProp(`${PREFIX}-forbidden`)}
            >
              {SSO_COPY.forbidden}
            </p>
          )}
        </div>
      </CardBody>

      <Dialog.Root
        open={confirming}
        onOpenChange={(next) => {
          // A dialog that vanishes mid-flight would leave an Owner unsure whether it ran.
          if (reset.isPending) return;
          setConfirming(next);
        }}
      >
        <Dialog.Content
          aria-label={SSO_COPY.resetConfirmTitle}
          className="flex flex-col gap-12"
          width={520}
          testId={`${PREFIX}-confirm-dialog`}
        >
          <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
            {SSO_COPY.resetConfirmTitle}
          </Dialog.Title>

          <Dialog.Description
            className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary"
            {...testIdProp(`${PREFIX}-confirm-scope`)}
          >
            {SSO_COPY.resetConfirmScope}
          </Dialog.Description>

          {/* ⚠ ONLY UNDER A STORED LOCK (AS-WEB22). At L0 nothing sends anyone to an identity
              provider, and at "Available for employees" their personal sign-in keeps working, so
              anywhere else this sentence would be a claim the product cannot substantiate. */}
          {mode === 'signin_lock' && (
            <p
              className="font-sans text-s14 font-w400 leading-h15 tracking-normal text-text-secondary"
              {...testIdProp(`${PREFIX}-confirm-idp`)}
            >
              {SSO_COPY_DRAFT.resetConfirmIdp}
            </p>
          )}

          <p
            className="font-sans text-s14 font-w400 leading-h15 tracking-normal text-text-secondary"
            {...testIdProp(`${PREFIX}-confirm-untouched`)}
          >
            {SSO_COPY.resetConfirmUntouched}
          </p>

          {/* The failure is rendered INSIDE the dialog so the ceremony is not dismissed by its own
              error — the same disposition the L4 lock ceremony takes. */}
          {reset.isError && !withdrawn && (
            <SsoErrorNote
              error={reset.error}
              fallback={SSO_COPY.resetFailed}
              testId={`${PREFIX}-confirm-error`}
            />
          )}

          <div className="flex items-center justify-end gap-12">
            <Button
              appearance="saas"
              tone="secondary"
              disabled={reset.isPending}
              onClick={() => setConfirming(false)}
              testId={`${PREFIX}-confirm-cancel`}
            >
              {SSO_COPY.resetCancel}
            </Button>
            <Button
              appearance="saas"
              tone="primary"
              loading={reset.isPending}
              onClick={() => reset.mutate()}
              testId={`${PREFIX}-confirm-submit`}
            >
              {SSO_COPY.resetConfirm}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Root>
    </Card>
  );
}

/**
 * What the run did.
 *
 * ★ THE INCOMPLETE CASE IS NOT THE ZERO CASE. A 200 whose body carried no numbers decodes to a
 * defined all-`null` outcome, and rendering that as three zeros would report a reset that signed
 * nobody out. It says what is known instead, and points at the same remedy.
 */
function ResetOutcome({ outcome }: { outcome: SessionResetOutcome }) {
  if (!resetCountsComplete(outcome)) {
    return (
      <p
        className="font-sans text-s13 font-w400 leading-h15 tracking-normal text-text-secondary"
        {...testIdProp(`${PREFIX}-incomplete`)}
      >
        {SSO_COPY.resetIncomplete}
      </p>
    );
  }

  const failed = resetHadFailures(outcome);
  return (
    <div
      className={
        failed
          ? 'flex flex-col gap-8 rounded-lg border border-warning-100 bg-warning-50 p-16'
          : 'flex flex-col gap-8 rounded-lg border border-border-subtle bg-surface-sunken p-16'
      }
      {...testIdProp(`${PREFIX}-outcome`)}
    >
      <p
        className={
          failed
            ? 'font-sans text-s14 font-w600 tracking-givmo text-warning-700'
            : 'font-sans text-s14 font-w600 tracking-givmo text-success-700'
        }
      >
        {SSO_COPY.resetDoneTitle}
      </p>
      <CountRow
        label={SSO_COPY.resetEmployeesLabel}
        value={outcome.employees}
        testId={`${PREFIX}-employees`}
      />
      <CountRow
        label={SSO_COPY.resetRevokedLabel}
        value={outcome.revoked}
        testId={`${PREFIX}-revoked`}
      />
      <CountRow
        label={SSO_COPY.resetFailedLabel}
        value={outcome.failed}
        testId={`${PREFIX}-failed`}
      />
      {/* The remedy rides the failure, never the clean run — "try again" beside a complete result
          would invite an Owner to sign their workforce out twice for nothing. */}
      {failed && (
        <p
          className="font-sans text-s13 font-w400 leading-h15 text-warning-700"
          {...testIdProp(`${PREFIX}-failed-remedy`)}
        >
          {SSO_COPY.resetFailedRemedy}
        </p>
      )}
    </div>
  );
}

function CountRow({
  label,
  value,
  testId,
}: {
  label: string;
  value: number | null;
  testId: string;
}) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-8">
      <span className="font-sans text-s13 text-text-secondary">{label}</span>
      <span
        className="font-sans text-s13 font-w600 tabular-nums text-text-strong"
        {...testIdProp(testId)}
      >
        {value === null ? '—' : value.toLocaleString('en-US')}
      </span>
    </div>
  );
}
